import * as React from 'react';
import { Button } from './button';
import { cn } from '@/lib/cn';

export interface SaveBarProps {
  /** True while a save is in flight. */
  saving: boolean;
  /** True when the form has unsaved changes. */
  dirty: boolean;
  /** Optional non-blocking message ("Zapisano" / "Niezapisane zmiany"). */
  status?: 'idle' | 'saved' | 'error';
  /** Callbacks. */
  onCancel?: () => void;
  /** Label for the primary action. Default: 'Zapisz'. */
  label?: string;
  className?: string;
}

/**
 * Persistent save bar pinned to the bottom of edit pages. Shows status,
 * primary save button, optional cancel-to-discard.
 */
export function SaveBar({
  saving,
  dirty,
  status = 'idle',
  onCancel,
  label = 'Zapisz zmiany',
  className,
}: SaveBarProps) {
  return (
    <div
      className={cn(
        'sticky bottom-0 -mx-6 -mb-6 mt-8 flex items-center justify-between gap-4 border-t border-border bg-bg/95 px-6 py-4 backdrop-blur md:-mx-8 md:px-8',
        className
      )}
      role="status"
      aria-live="polite"
    >
      <p className="text-sm">
        {saving ? (
          <span className="text-text-muted">Zapisywanie…</span>
        ) : dirty ? (
          <span className="text-warning">Niezapisane zmiany</span>
        ) : status === 'saved' ? (
          <span className="text-success">Zapisano</span>
        ) : status === 'error' ? (
          <span className="text-error">Błąd zapisu — spróbuj ponownie</span>
        ) : (
          <span className="text-text-subtle">Wszystko zapisane</span>
        )}
      </p>
      <div className="flex items-center gap-3">
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={!dirty || saving}>
            Odrzuć zmiany
          </Button>
        ) : null}
        <Button type="submit" disabled={!dirty || saving}>
          {label}
        </Button>
      </div>
    </div>
  );
}
